import React from 'react';
import Icon from '../../../components/AppIcon';

const DietarySummaryPanel = ({ selectedGuests }) => {
  // Mock guest data (same as in GuestSelectionPanel)
  const mockGuests = [
    {
      id: 1,
      name: "Sarah Johnson",
      dietaryRestrictions: ["Vegetarian", "Gluten-Free"],
      allergies: ["Nuts"],
      preferences: ["Italian", "Mediterranean"]
    },
    {
      id: 2,
      name: "Michael Johnson",
      dietaryRestrictions: [],
      allergies: ["Shellfish"],
      preferences: ["BBQ", "American"]
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      dietaryRestrictions: ["Vegan"],
      allergies: [],
      preferences: ["Mexican", "Asian"]
    },
    {
      id: 4,
      name: "David Chen",
      dietaryRestrictions: [],
      allergies: ["Dairy"],
      preferences: ["Chinese", "Thai"]
    },
    {
      id: 5,
      name: "Lisa Chen",
      dietaryRestrictions: ["Pescatarian"],
      allergies: [],
      preferences: ["Japanese", "Seafood"]
    },
    {
      id: 6,
      name: "Robert Williams",
      dietaryRestrictions: ["Keto"],
      allergies: [],
      preferences: ["Steakhouse", "Low-carb"]
    }
  ];

  const selectedGuestData = mockGuests?.filter(guest => selectedGuests?.includes(guest?.id));

  if (selectedGuestData?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Dietary Summary</h3>
        <div className="text-center py-8">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Select guests to see dietary requirements</p>
        </div>
      </div>
    );
  }

  // Aggregate dietary information
  const allRestrictions = selectedGuestData?.flatMap(guest => guest?.dietaryRestrictions);
  const allAllergies = selectedGuestData?.flatMap(guest => guest?.allergies);
  const allPreferences = selectedGuestData?.flatMap(guest => guest?.preferences);

  const uniqueRestrictions = [...new Set(allRestrictions)];
  const uniqueAllergies = [...new Set(allAllergies)];
  const uniquePreferences = [...new Set(allPreferences)];

  // Count occurrences for preferences
  const preferenceCount = allPreferences?.reduce((acc, pref) => {
    acc[pref] = (acc?.[pref] || 0) + 1;
    return acc;
  }, {});

  const sortedPreferences = Object.entries(preferenceCount)?.sort(([,a], [,b]) => b - a)?.map(([pref, count]) => ({ name: pref, count }));

  // Identify potential conflicts
  const hasVegan = uniqueRestrictions?.includes('Vegan');
  const hasVegetarian = uniqueRestrictions?.includes('Vegetarian');
  const hasPescatarian = uniqueRestrictions?.includes('Pescatarian');
  const hasKeto = uniqueRestrictions?.includes('Keto');

  const conflicts = [];
  if (hasVegan && (hasPescatarian || (!hasVegetarian && !hasPescatarian))) {
    conflicts?.push("Vegan guests require plant-based options only");
  }
  if (hasKeto && (hasVegan || hasVegetarian)) {
    conflicts?.push("Balancing Keto (high-fat, low-carb) with plant-based diets");
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Dietary Summary</h3>
      <div className="space-y-6">
        {/* Dietary Restrictions */}
        {uniqueRestrictions?.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Shield" size={16} className="text-warning" />
              <h4 className="font-medium text-foreground">Dietary Restrictions</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {uniqueRestrictions?.map((restriction, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-warning/10 text-warning-foreground border border-warning/20"
                >
                  {restriction}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Allergies */}
        {uniqueAllergies?.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="AlertTriangle" size={16} className="text-error" />
              <h4 className="font-medium text-foreground">Allergies</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {uniqueAllergies?.map((allergy, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-error/10 text-error-foreground border border-error/20"
                >
                  <Icon name="AlertCircle" size={12} className="mr-1" />
                  {allergy}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Cuisine Preferences */}
        {sortedPreferences?.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Heart" size={16} className="text-success" />
              <h4 className="font-medium text-foreground">Popular Cuisines</h4>
            </div>
            <div className="space-y-2">
              {sortedPreferences?.slice(0, 5)?.map((pref, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{pref?.name}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-success rounded-full"
                        style={{ width: `${(pref?.count / selectedGuestData?.length) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right">
                      {pref?.count}/{selectedGuestData?.length}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Potential Conflicts */}
        {conflicts?.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <h4 className="font-medium text-foreground">Menu Planning Considerations</h4>
            </div>
            <div className="space-y-2">
              {conflicts?.map((conflict, index) => (
                <div key={index} className="flex items-start space-x-2 p-3 bg-warning/5 rounded-lg border border-warning/20">
                  <Icon name="Info" size={14} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-warning-foreground">{conflict}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-semibold text-primary">{selectedGuestData?.length}</p>
              <p className="text-sm text-muted-foreground">Guests</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-warning">{uniqueRestrictions?.length + uniqueAllergies?.length}</p>
              <p className="text-sm text-muted-foreground">Restrictions</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-success">{uniquePreferences?.length}</p>
              <p className="text-sm text-muted-foreground">Cuisines</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietarySummaryPanel;